import { Injectable } from '@angular/core';
import { AuthPolicyService } from '@core/auth/auth-policy.service';
import {
    Observable,
    Observer,
    map,
    of,
    take,
    withLatestFrom,
    switchMap,
    catchError,
    combineLatest,
    filter
} from 'rxjs';
import { Store } from '@ngrx/store';

import { AccountRole } from '@core/models/account/account-role';
import { AuthCredentials } from '@core/models/auth/auth-credentials';
import { AuthDataService } from '@core/data/api/auth/auth-data.service';
import { AuthSession } from '@core/models/auth/auth-session';
import { environment } from '@environments/environment';
import { IObservableAction } from '../shared/actions/observable-action';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { LoginAction, LoginCompanyAction, LogoutAction, PostLogoutAction, LoginSSOAction } from '../login/login-actions';
import { getCompanyAuthentication, getUserAuthentication } from './auth-selectors';
import { getCompanyFeatures } from '../session/session-selectors';
import { SessionService } from '../session/session.service';
import { isAuthenticated, isUserAuthenticated } from '../auth/auth-selectors';
import { RouteType } from '@core/router/route-type';
import { SetUserAuthAction, SetCompanyAuthAction, RequestVerificationCodeAction, SubmitVerificationCodeAction, ResetPasswordAction } from './auth-actions';
import { ContinueAuthenticateDictionary } from '@core/models/auth/continue-authenticate-dictionary';
import { AuthTokenType } from '@core/models/auth/auth-token-type';
import { AccountType } from '@core/models/account/account-type';
import { ConnectionPermission } from '@core/models/connection/connection-permission';
import { Company } from '@core/models/common/company';
import { FeaturePolicyService } from '@core/feature/feature-policy.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    constructor(
        private store$: Store<any>,
        private authPolicyService: AuthPolicyService,
        private featurePolicyService: FeaturePolicyService,
        private localStorageService: LocalStorageService,
        private authDataService: AuthDataService,
        private sessionService: SessionService
    ) {}

    /**
     * Defines HTTP headers for authorization of the current session.
     * @returns {{}}
     */
    getAuthorizationHeader(): string {
        let authHeader: string = '';
        let authSession: AuthSession;

        // Grab the root auth
        this.store$.select(getCompanyAuthentication)
            .pipe(take(1))
            .subscribe((session) => {
                authSession = session;
            });

        if (authSession && authSession.access_token) {
            authHeader = 'Bearer ' + authSession.access_token;

        } else {

            this.store$.select(getUserAuthentication)
                .pipe(take(1))
                .subscribe((session) => {
                    authSession = session;
                });

            if (authSession && authSession.access_token) {
                authHeader = 'Bearer ' + authSession.access_token;
            }
        }

        return authHeader;
    }

    getUserAuthentication(): Observable<AuthSession> {
        return this.store$.select(getUserAuthentication);
    }

    getCompanyAuthorization(): Observable<string> {
        return this.store$.select(getCompanyAuthentication)
            .pipe(map((authSession: AuthSession) => {
                return authSession?.access_token || null;
            }));
    }

    isDevSupport(): Observable<boolean> {
        return this.getUserAuthentication()
            .pipe(map((userAuth: AuthSession) => {
                return userAuth.role === AccountRole.SUPPORT;
            }));
    }

    /**
     * Defines HTTP headers for authorization of the root session.
     * @returns {{}}
     */
    getUserAuthorizationHeader(): string {
        let authHeader: string = '';
        let userAuthSession: AuthSession;

        // Grab the root auth
        this.store$.select(getUserAuthentication)
            .pipe(take(1))
            .subscribe((authSession) => {
                userAuthSession = authSession;
            });

        if (userAuthSession && userAuthSession.access_token) {
            authHeader = 'Bearer ' + userAuthSession.access_token;
        }

        return authHeader;
    }

    login(authCredentials: AuthCredentials): IObservableAction {
        const action: any = new LoginAction(authCredentials);
        this.store$.dispatch(action);
        return action;
    }

    loginCompany(companyId: string, isChangingCompanies: boolean = false, bypassRedirection: boolean = false): IObservableAction {
        const action: any = new LoginCompanyAction({ companyId, isChangingCompanies, bypassRedirection });
        this.store$.dispatch(action);
        return action;
    }

    /**
     * Login via single sign-on.
     * @param ssoId
     * @param ssoProvider
     */
    loginViaSSO(ssoId: string, ssoProvider: string): IObservableAction {
        const action$: any = new LoginSSOAction({ ssoId, ssoProvider });
        this.store$.dispatch(action$);

        return action$;
    }

    logout() {
        this.store$.dispatch(new LogoutAction());
    }

    isAuthenticated() {
        return this.store$.select(isAuthenticated);
    }

    isUserAuthenticated() {
        return this.store$.select(isUserAuthenticated);
    }

    isValidRoute(path: string, routeType: RouteType): boolean {
        const matchSegments = path.split('/');
        const routes =
            environment.routes.hasOwnProperty(routeType) &&
            environment.routes[routeType];

        return !!routes.filter((route) => {
            let isValid = false;
            const matchRoute = route;
            if (route.charAt(0) === '/') {
                // Validate absolute matches
                if (route === '/') {
                    isValid = path === route;
                } else {
                    isValid = path.indexOf(matchRoute) === 0;
                }
            } else {
                // Validate relative matches
                isValid =
                    matchSegments && matchSegments.indexOf(matchRoute) >= 0;
            }

            return isValid;
        }).length;
    }

    isFeatureAvailable(featureId: string): Observable<boolean> {
        return this.store$.select(getCompanyFeatures)
                .pipe(map((features) =>
                    this.featurePolicyService.isFeatureAvailable(
                        features,
                        featureId
                    )
                ));
    }

    setAuth(authSession: AuthSession) {
        this.store$.dispatch(new SetUserAuthAction(authSession));
    }

    setRootAuth(authSession: AuthSession) {
        this.store$.dispatch(new SetCompanyAuthAction(authSession));
    }

    refreshToken2(isChangingCompanies?: boolean) {

        // observer: Observer<any>
        return new Observable((observer: Observer<any>) => {
            
            this.store$.select(getCompanyAuthentication)
                .pipe(
                    withLatestFrom(
                        this.store$.select(getUserAuthentication),
                    ),
                    take(1)
                ).subscribe(([auth, userAuth]: [AuthSession, AuthSession]) => {

                    // Attempt to refresh token as a company
                    this.authDataService.loginByRefreshToken( isChangingCompanies || !auth.refresh_token ?  userAuth.refresh_token : auth.refresh_token)
                        .pipe(
                            catchError((error) => {

                                if (isChangingCompanies || error?.status === 401) {
                                    observer.error(error);
    
                                    this.store$.dispatch(new PostLogoutAction());

                                    return of(error);
                                } else if (auth?.refresh_token) {
                                    const cad: ContinueAuthenticateDictionary = userAuth.cad;

                                    return this.authDataService.loginByAccessToken(cad.company_id, userAuth)
                                        .pipe(
                                            switchMap((companyLoginResponse) => {
                                                    this.store$.dispatch(new SetCompanyAuthAction(companyLoginResponse));
            
                                                    observer.next(null);
                                                    observer.complete();
    
                                                return []
            
                                            }),
                                            catchError((companyLoginError) => {
    
                                                return this.authDataService.loginByRefreshToken(userAuth.refresh_token)
                                                    .pipe(
    
                                                        switchMap(userRefreshTokenResponse => {
                                                            const userRefreshCad = userAuth.cad;
                                                            const newUserAuth: AuthSession = {
                                                                ...userAuth,
                                                                ...userRefreshTokenResponse
                                                            };
    
                                                            this.store$.dispatch(new SetUserAuthAction(newUserAuth));
    
                                                            return this.authDataService.loginByAccessToken(userRefreshCad.company_id, newUserAuth)
                                                                .pipe(
                                                                    switchMap((newCompanyLoginResponse) => {
    
                                                                        this.store$.dispatch(new SetCompanyAuthAction(newCompanyLoginResponse));
    
                                                                        observer.next(null);
                                                                        observer.complete();
    
                                                                        return [];
                                                                    }),
                                                                    catchError((newCompanyLoginError) => {
    
                                                                        alert('please log in again');
    
                                                                        observer.error(null);
    
                                                                        this.store$.dispatch(new PostLogoutAction());
    
                                                                        return of(newCompanyLoginError);
                                                                    })
                                                                );
                                                        }),
                                                        catchError((userRefreshTokenError) => {
    
                                                            observer.error(null);
    
                                                            // Note: This is the end of the line. company access, company refresh, user access & user refresh have failed
    
                                                            this.store$.dispatch(new PostLogoutAction());
    
                                                            return of(userRefreshTokenError);
                                                        })
                                                    );
    
    
                                                
                                            })
                                        );
                                } else {
                                    observer.error(error);
                                    return of(error);
                                }
                                
                            })
                        )
                        .subscribe((refreshTokenResponse) => {

                            if (isChangingCompanies) {

                                const userRefreshToken: AuthSession = {
                                    ...userAuth,
                                    access_token: refreshTokenResponse.access_token,
                                    refresh_token: refreshTokenResponse.refresh_token,
                                    expires_in: refreshTokenResponse.expires_in
                                }

                                this.store$.dispatch(new SetUserAuthAction(userRefreshToken));
                                this.store$.dispatch(new SetCompanyAuthAction({
                                    access_token: '',
                                    refresh_token: '',
                                    token_type: AuthTokenType.BEARER,
                                    expires_in: 0,
                                    _profile_type: AccountType.COMPANY,
                                    _profile_id: '',
                                }));

                            } else {
                                const rootAuthSession: AuthSession = {
                                    ...refreshTokenResponse,
                                    _profile_type: AccountType.COMPANY
                                };
    
                                this.store$.dispatch(new SetCompanyAuthAction(refreshTokenResponse));
                            }
                            
                            observer.next(null);
                            observer.complete();
                        });
                });
        });
    }

    refreshToken(): Observable<any> {

        return of((observer: Observer<any>) => {
            const w: any = window;

            return this.store$.select(getUserAuthentication)
                    .pipe(
                        withLatestFrom(
                            this.store$.select(isAuthenticated),
                            this.store$.select(getCompanyAuthentication)
                        ),
                        switchMap(([authSession, isAuthenticated, rootAuthentication]: 
                            [AuthSession, boolean, AuthSession]) => {

                            return this.authDataService.loginByRefreshToken(authSession.refresh_token)
                                    .pipe(
                                        switchMap((refreshTokenResponse) => {

                                            observer.next(refreshTokenResponse);
                                            observer.complete();
                                            return [];
                                        }),
                                        catchError((error) => {
                                            observer.error('');
                                            return of(error);
                                        })
                                    );
                        }));
                    });
    }

    requestVerificationCode(request: { email?: string, phone?: string }): IObservableAction {
        const action: any = new RequestVerificationCodeAction(request);
        this.store$.dispatch(action);
        return action;
    }

    submitVerificationCode(code: string, id: string): IObservableAction {
        const action: any = new SubmitVerificationCodeAction({ code, id });
        this.store$.dispatch(action);
        return action;
    }

    resetPassword(password: string, id: string, code: string): IObservableAction {
        const action: any = new ResetPasswordAction({ code, id, password });
        this.store$.dispatch(action);
        return action;
    }

    isAuthorised(authId: string): Observable<boolean> {
        let isCompany: boolean = false;
        return combineLatest([
            this.sessionService.getCompany(),
            this.sessionService.getPermissions()
        ]).pipe(
            filter(([company, permissions]) => !!company && !!permissions),
            map(([company, permissions]: 
                [Company, ConnectionPermission[]]) => {
                return this.authPolicyService.isCompanyAuthenticated(
                    company,
                    permissions,
                    authId
                )
            })
        );
    }
}
